import React, { useState } from 'react';
import { Box, CircularProgress, Alert, Grid, Paper, Typography } from '@mui/material';
import { useDeviceMetricsByIdQuery } from '../api/JsdhNotifikaceService/service/DeviceMetricsMutations';
import type { MetricsTimeRange } from '../api/JsdhNotifikaceService/generated/models/DeviceMetrics';
import {
    TimeRangeSelector,
    TemperatureChart,
    CpuLoadChart,
    MemoryChart,
    DiskChart,
    WifiSignalChart,
} from './DeviceMetricsCharts';

interface DeviceMetricsTabProps {
    deviceId: number | null;
}

const DeviceMetricsTab: React.FC<DeviceMetricsTabProps> = ({ deviceId }) => {
    const [timeRange, setTimeRange] = useState<MetricsTimeRange>('24h');
    const { data: metrics, isLoading, error } = useDeviceMetricsByIdQuery(deviceId, timeRange);

    if (!deviceId) {
        return <Alert severity="warning">Neplatne ID zarizeni.</Alert>;
    }

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error">Chyba pri nacitani metrik: {error.message}</Alert>;
    }

    if (!metrics || metrics.length === 0) {
        return <Alert severity="info">Pro toto zarizeni nejsou dostupne zadne metriky.</Alert>;
    }

    const hasWifiData = metrics.some((m) => m.wifiSignalDbm !== null);

    return (
        <Box>
            <Box display="flex" justifyContent="flex-end" mb={2}>
                <TimeRangeSelector value={timeRange} onChange={setTimeRange} />
            </Box>

            <Grid container spacing={2}>
                {/* Temperature Chart */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                            Teplota
                        </Typography>
                        <TemperatureChart data={metrics} timeRange={timeRange} />
                    </Paper>
                </Grid>

                {/* CPU Load Chart */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                            Zatizeni CPU
                        </Typography>
                        <CpuLoadChart data={metrics} timeRange={timeRange} />
                    </Paper>
                </Grid>

                {/* Memory Chart */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                            Pamet RAM
                        </Typography>
                        <MemoryChart data={metrics} timeRange={timeRange} />
                    </Paper>
                </Grid>

                {/* Disk Chart */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                            Disk
                        </Typography>
                        <DiskChart data={metrics} timeRange={timeRange} />
                    </Paper>
                </Grid>

                {/* WiFi Signal Chart (conditional) */}
                {hasWifiData && (
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Paper sx={{ p: 2 }}>
                            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                                WiFi Signal
                            </Typography>
                            <WifiSignalChart data={metrics} timeRange={timeRange} />
                        </Paper>
                    </Grid>
                )}
            </Grid>
        </Box>
    );
};

export default DeviceMetricsTab;
